import { CancelTokenSource } from "axios";
import { MutationTree } from "vuex";
import { ClientProject } from "@/models/ClientProject";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { AdTitlesByLeads, Assignee, ChannelMultiselectItem, MultiselectItem, SelectListItem } from "@/models/MultiselectItem";
import { TableSettings } from "@/models/TableSettings";
import { ContactForm, LeadClientEditViewModel, LeadEditViewModel, LeadsPageViewModel, RefreshGridMode, PhonePopupData } from "@/models/leads/ContactForm";
import { LeadStatistic, LeadStatisticExtended, PhoneLeadStats } from "@/models/leads/LeadStatistic";
import { LeadStatus } from "@/models/leads/LeadStatus";
import { LeadsState } from "@/models/states/LeadsState";
import { LeadsPayload } from "@/services/api/ApiService";
import { getDefaultState } from "./index";

const mutations: MutationTree<LeadsState> = {
  logout(state: LeadsState) {
    state.selectedLeadGridColumnsIds = [];
    state.showGraphs = true;
  },

  resetState(state: LeadsState) {
    Object.assign(state, getDefaultState())
  },

  setAdGroups(state: LeadsState, adGroups: MultiselectItem[]) {
    state.adGroups = adGroups;
  },

  setAdTitles(state: LeadsState, adTitles: AdTitlesByLeads[]) {
    state.adTitles = adTitles;
  },

  setAdditionalStatuses(state: LeadsState, additionalStatuses: SelectListItem[]) {
    state.additionalStatuses = additionalStatuses;
  },

  setAssets(state: LeadsState, assets: ClientProject[]) {
    state.assets = assets;
  },

  setAssignees(state: LeadsState, assignees: Assignee[]) {
    state.assignees = assignees;
  },

  setChannels(state: LeadsState, channels: ChannelMultiselectItem[]) {
    state.channels = channels;
  },

  setClients(state: LeadsState, clients: MultiselectItem[]) {
    state.clients = clients;
  },

  setColumns(state: LeadsState, columns: GridColumn[]) {
    state.columns = columns;
  },

  setColumnsFilters(state: LeadsState, columnsFilters: FilterCollection) {
    state.columnsFilters = columnsFilters;
  },

  setCommitments(state: LeadsState, commitments: MultiselectItem[]) {
    state.commitments = commitments;
  },

  setDatePeriod(state: LeadsState, datePeriod: Date[]) {
    state.datePeriod = datePeriod;
  },

  setDeactivateStats(state: LeadsState, deactivateStats: boolean) {
    state.deactivateStats = deactivateStats;
  },

  setGraphsHiddenOnScroll(state: LeadsState, graphsHiddenOnScroll: boolean) {
    state.graphsHiddenOnScroll = graphsHiddenOnScroll;
  },

  setLeadClientEditViewModel(state: LeadsState, leadClientEditViewModel: LeadClientEditViewModel) {
    state.leadClientEditViewModel = leadClientEditViewModel;
  },

  setLeadClientsDatePeriod(state: LeadsState, datePeriod: Date[]) {
    state.leadClientsDatePeriod = datePeriod;
  },

  setLeadClientsSearchValue(state: LeadsState, searchValue: string) {
    state.leadClientsSearchValue = searchValue;
  },

  setLeadClientsSelectedAdditionalStatusesIds(state: LeadsState, selectedAdditionalStatusesIds: number[]) {
    state.leadClientsSelectedAdditionalStatusesIds = selectedAdditionalStatusesIds;
  },

  setLeadClientsSelectedAdGroupsIds(state: LeadsState, selectedAdGroupsIds: string[]) {
    state.leadClientsSelectedAdGroupsIds = selectedAdGroupsIds;
  },

  setLeadClientsSelectedAdTitles(state: LeadsState, selectedAdTitles: string[]) {
    state.leadClientsSelectedAdTitles = selectedAdTitles;
  },

  setLeadClientsSelectedAssetsIds(state: LeadsState, selectedAssetsIds: string[]) {
    state.leadClientsSelectedAssetsIds = selectedAssetsIds;
  },

  setLeadClientsSelectedAssigneesIds(state: LeadsState, selectedAssigneesIds: number[]) {
    state.leadClientsSelectedAssigneesIds = selectedAssigneesIds;
  },

  setLeadClientsSelectedCallStatusesIds(state: LeadsState, selectedCallStatusesIds: number[]) {
    state.leadClientsSelectedCallStatusesIds = selectedCallStatusesIds;
  },

  setLeadClientsSelectedChannelMetadataTypesIds(state: LeadsState, selectedChannelMetadataTypesIds: number[]) {
    state.leadClientsSelectedChannelMetadataTypesIds = selectedChannelMetadataTypesIds;
  },

  setLeadClientsSelectedChannelsIds(state: LeadsState, selectedChannelsIds: number[]) {
    state.leadClientsSelectedChannelsIds = selectedChannelsIds;
  },

  setLeadClientsSelectedClientsIds(state: LeadsState, selectedClientsIds: number[]) {
    state.leadClientsSelectedClientsIds = selectedClientsIds;
  },

  setLeadClientsSelectedCommitmentsIds(state: LeadsState, selectedCommitmentsIds: number[]) {
    state.leadClientsSelectedCommitmentsIds = selectedCommitmentsIds;
  },

  setLeadClientsSelectedFoundMetadataTypesIds(state: LeadsState, selectedFoundMetadataTypesIds: number[]) {
    state.leadClientsSelectedFoundMetadataTypesIds = selectedFoundMetadataTypesIds;
  },

  setLeadClientsSelectedLeadStatusesIds(state: LeadsState, selectedLeadStatusesIds: LeadStatus[]) {
    state.leadClientsSelectedLeadStatusesIds = selectedLeadStatusesIds;
  },

  setLeadClientsSelectedPriceType(state: LeadsState, selectedPriceType: string) {
    state.leadClientsSelectedPriceType = selectedPriceType;
  },

  setLeadClientsSelectedSaleStatusesIds(state: LeadsState, selectedSaleStatusesIds: LeadStatus[]) {
    state.leadClientsSelectedSaleStatusesIds = selectedSaleStatusesIds;
  },

  setLeadClientsSelectedTags(state: LeadsState, selectedTags: string[]) {
    state.leadClientsSelectedTags = selectedTags;
  },

  setLeadEditViewModel(state: LeadsState, leadEditViewModel: LeadEditViewModel) {
    state.leadEditViewModel = leadEditViewModel;
  },

  setLeads(state: LeadsState, leads: DataSourceResult) {
    state.leads = leads;
  },

  setLeadsCancelTokenSource(state: LeadsState, cancelTokenSource: CancelTokenSource) {
    state.leadsCancelTokenSource = cancelTokenSource;
  },

  setLeadsLoading(state: LeadsState, leadsLoading: boolean) {
    state.leadsLoading = leadsLoading;
  },

  setLeadsPageData(state: LeadsState, leadsPageData: LeadsPageViewModel) {
    state.leadsPageData = leadsPageData;
  },

  setLeadsPayload(state: LeadsState, leadsPayload: LeadsPayload) {
    state.leadsPayload = leadsPayload;
  },

  setLeadsStatisticByAdditionalStatus(state: LeadsState, leadStatistics: LeadStatistic[]) {
    state.leadsStatisticByAdditionalStatus = leadStatistics;
  },

  setLeadsStatisticByCallStatus(state: LeadsState, leadStatistics: LeadStatistic[]) {
    state.leadsStatisticByCallStatus = leadStatistics;
  },

  setLeadsStatisticByCampaigns(state: LeadsState, leadStatistics: LeadStatistic[]) {
    state.leadsStatisticByCampaigns = leadStatistics;
  },

  setLeadsStatisticByPhoneAndForms(state: LeadsState, leadStatistics: LeadStatistic[]) {
    state.leadsStatisticByPhoneAndForms = leadStatistics;
  },

  setLeadsStatisticBySaleStatus(state: LeadsState, leadStatistics: LeadStatistic[]) {
    state.leadsStatisticBySaleStatus = leadStatistics;
  },

  setLeadsStatisticChannelMetadataType(state: LeadsState, leadStatistics: LeadStatistic[]) {
    state.leadsStatisticChannelMetadataType = leadStatistics;
  },

  setLeadsStatisticDealMeetingsLeads(state: LeadsState, leadStatistics: LeadStatisticExtended[]) {
    state.leadsStatisticDealMeetingsLeads = leadStatistics;
  },

  setLeadsStatisticPhones(state: LeadsState, leadsStatisticPhones: PhoneLeadStats) {
    state.leadsStatisticPhones = leadsStatisticPhones;
  },

  setNeedToRefreshLeadsGrid(state: LeadsState, needToRefreshLeadsGrid: RefreshGridMode) {
    state.needToRefreshLeadsGrid = needToRefreshLeadsGrid;
  },

  setPage(state: LeadsState, page: number) {
    state.page = page;
  },

  setSearchValue(state: LeadsState, searchValue: string) {
    state.searchValue = searchValue;
  },

  setSelectedAdditionalStatusesIds(state: LeadsState, selectedAdditionalStatusesIds: number[]) {
    state.selectedAdditionalStatusesIds = selectedAdditionalStatusesIds;
  },

  setSelectedAdGroupsIds(state: LeadsState, selectedAdGroupsIds: string[]) {
    state.selectedAdGroupsIds = selectedAdGroupsIds;
  },

  setSelectedAdTitles(state: LeadsState, selectedAdTitles: string[]) {
    state.selectedAdTitles = selectedAdTitles;
  },

  setSelectedAssetsIds(state: LeadsState, selectedAssetsIds: string[]) {
    state.selectedAssetsIds = selectedAssetsIds;
  },

  setSelectedAssigneesIds(state: LeadsState, selectedAssigneesIds: number[]) {
    state.selectedAssigneesIds = selectedAssigneesIds;
  },

  setSelectedCallStatusesIds(state: LeadsState, selectedCallStatusesIds: number[]) {
    state.selectedCallStatusesIds = selectedCallStatusesIds;
  },

  setSelectedChannelMetadataTypesIds(state: LeadsState, selectedChannelMetadataTypesIds: number[]) {
    state.selectedChannelMetadataTypesIds = selectedChannelMetadataTypesIds;
  },

  setSelectedChannelsIds(state: LeadsState, selectedChannelsIds: number[]) {
    state.selectedChannelsIds = selectedChannelsIds;
  },

  setSelectedClientsIds(state: LeadsState, selectedClientsIds: number[]) {
    state.selectedClientsIds = selectedClientsIds;
  },

  setSelectedCommitmentsIds(state: LeadsState, selectedCommitmentsIds: number[]) {
    state.selectedCommitmentsIds = selectedCommitmentsIds;
  },

  setSelectedFoundMetadataTypesIds(state: LeadsState, selectedFoundMetadataTypesIds: number[]) {
    state.selectedFoundMetadataTypesIds = selectedFoundMetadataTypesIds;
  },

  setSelectedLead(state: LeadsState, selectedLead?: ContactForm) {
    state.selectedLead = selectedLead;
  },

  setSelectedLeadClient(state: LeadsState, selectedLeadClient?: ContactForm) {
    state.selectedLeadClient = selectedLeadClient;
  },

  setSelectedLeadId(state: LeadsState, leadClientId: number): void {
    state.selectedLeadClientId = leadClientId;
  },

  setSelectedLeadGridColumnsIds(state: LeadsState, selectedLeadGridColumnsIds: number[]) {
    state.selectedLeadGridColumnsIds = selectedLeadGridColumnsIds;
  },

  setSelectedLeadStatusesIds(state: LeadsState, selectedLeadStatusesIds: LeadStatus[]) {
    state.selectedLeadStatusesIds = selectedLeadStatusesIds;
  },

  setSelectedPriceType(state: LeadsState, selectedPriceType: string) {
    state.selectedPriceType = selectedPriceType;
  },

  setSelectedSaleStatusesIds(state: LeadsState, selectedSaleStatusesIds: LeadStatus[]) {
    state.selectedSaleStatusesIds = selectedSaleStatusesIds;
  },

  setSelectedTags(state: LeadsState, selectedTags: string[]) {
    state.selectedTags = selectedTags;
  },

  setShowDeleted(state: LeadsState, showDeleted: boolean) {
    state.showDeleted = showDeleted;
  },  

  setShowDeletedClientLeads(state: LeadsState, showDeletedClientLeads: boolean) {
    state.showDeletedClientLeads = showDeletedClientLeads;
  },

  setShowFavourite(state: LeadsState, showFavourite: boolean) {
    state.showFavourite = showFavourite;
  },

  setShowFavouriteClientLeads(state: LeadsState, showFavouriteClientLeads: boolean) {
    state.showFavouriteClientLeads = showFavouriteClientLeads;
  },

  setShowGraphs(state: LeadsState, showGraphs: boolean) {
    state.showGraphs = showGraphs;
  },

  setShowGraphsClientLeads(state: LeadsState, showGraphsClientLeads: boolean) {
    state.showGraphsClientLeads = showGraphsClientLeads;
  },

  setShowNotActiveCampaign(state: LeadsState, showNotActiveCampaign: boolean) {
    state.showNotActiveCampaign = showNotActiveCampaign;
  },

  setShowNotActiveCampaignClientLeads(state: LeadsState, showNotActiveCampaignClientLeads: boolean) {
    state.showNotActiveCampaignClientLeads = showNotActiveCampaignClientLeads;
  },

  setSortDir(state: LeadsState, sortDir: string) {
    state.sortDir = sortDir;
  },

  setSortField(state: LeadsState, sortField: string) {
    state.sortField = sortField;
  },

  setTableSettings(state: LeadsState, tableSettings: TableSettings) {
    state.tableSettings = tableSettings;
  },

  setTags(state: LeadsState, tags: MultiselectItem[]) {
    state.tags = tags;
  },

  setUniqueByCampaign(state: LeadsState, uniqueByCampaign: boolean) {
    state.uniqueByCampaign = uniqueByCampaign;
  },

  setUniqueByCampaignClientLeads(state: LeadsState, uniqueByCampaignClientLeads: boolean) {
    state.uniqueByCampaignClientLeads = uniqueByCampaignClientLeads;
  },

    setPhonePopupData(state: LeadsState, phonePopupData: PhonePopupData) {
        state.phonePopupData = phonePopupData;
    },

    setHidePhonePopup(state: LeadsState, hidePopup: boolean) {
        state.phonePopupData.showPopup = !hidePopup;
    },
};
export default mutations;
